import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import JSZip from "jszip";
import { toBlob } from "html-to-image";
import {
  Plus, Trash2, Printer, Ban, KeyRound, CheckSquare, Square, Send, Download,
  Globe, Copy, MessageCircle, Image as ImageIcon, ChevronDown, ChevronRight, FileImage, QrCode, Loader2, Zap, Search, Link2, AlertTriangle, X,
} from "lucide-react";
import { api, API, LOGO_URL } from "../lib/api";

const fmt = (n) => (n || 0).toLocaleString("ru-RU");
const fmtDT = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (e) { return iso.slice(0, 16).replace("T", " "); }
};
const fmtD = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("ru-RU"); } catch (e) { return iso.slice(0, 10); }
};

const STATUS = {
  active: ["Действителен", "bg-green-100 text-green-700"],
  redeemed: ["Использован", "bg-gray-200 text-gray-500"],
  void: ["Аннулирован", "bg-red-100 text-red-600"],
};

// CRAFT color schemes for certificate flyers
export const CERT_STYLES = {
  red: {
    label: "Красный классик", swatch: "#E30000",
    bg: "linear-gradient(120deg, #FF1F1F 0%, #E30000 55%, #C40000 100%)",
    title: "#FEBC10", amountBg: "#FEBC10", amountText: "#000000",
    brand: "#FFFFFF", brandSub: "rgba(255,255,255,0.7)",
    text: "rgba(255,255,255,0.88)", sub: "rgba(255,255,255,0.55)",
    pillBg: "#FEBC10", pillText: "#000000", fade: "#E30000", deco: "rgba(255,255,255,0.05)", codeBorder: "none",
  },
  yellow: {
    label: "Жёлтый CRAFT", swatch: "#FEBC10",
    bg: "linear-gradient(120deg, #FFD84D 0%, #FEBC10 55%, #F0A800 100%)",
    title: "#E30000", amountBg: "#E30000", amountText: "#FFFFFF",
    brand: "#111111", brandSub: "rgba(0,0,0,0.55)",
    text: "rgba(0,0,0,0.8)", sub: "rgba(0,0,0,0.5)",
    pillBg: "#E30000", pillText: "#FFFFFF", fade: "#FEBC10", deco: "rgba(255,255,255,0.25)", codeBorder: "none",
  },
  dark: {
    label: "Тёмный", swatch: "#161616",
    bg: "linear-gradient(120deg, #2A2A2A 0%, #161616 55%, #000000 100%)",
    title: "#FEBC10", amountBg: "#FEBC10", amountText: "#000000",
    brand: "#FFFFFF", brandSub: "rgba(255,255,255,0.6)",
    text: "rgba(255,255,255,0.85)", sub: "rgba(255,255,255,0.45)",
    pillBg: "#E30000", pillText: "#FFFFFF", fade: "#161616", deco: "rgba(255,255,255,0.06)", codeBorder: "none",
  },
  light: {
    label: "Светлый крафт", swatch: "#FBF3E4",
    bg: "linear-gradient(120deg, #FDF8EC 0%, #FBF3E4 55%, #F1E3C8 100%)",
    title: "#E30000", amountBg: "#E30000", amountText: "#FFFFFF",
    brand: "#111111", brandSub: "rgba(0,0,0,0.5)",
    text: "rgba(0,0,0,0.75)", sub: "rgba(0,0,0,0.45)",
    pillBg: "#FEBC10", pillText: "#000000", fade: "#FBF3E4", deco: "rgba(227,0,0,0.05)", codeBorder: "1px solid #E5D9BE",
  },
};

// A6 landscape gift-certificate flyer: 148mm x 105mm
export const CertCard = ({ cert, domain = "craft-oila.uz", styleKey = "red", bgImage = null, bgFull = false, bgScrim = true, logoSrc = LOGO_URL }) => {
  const base = CERT_STYLES[styleKey] || CERT_STYLES.red;
  // In full-background mode text sits on a dark scrim — force light text colors
  const s = bgFull ? {
    ...base,
    title: "#FEBC10", brand: "#FFFFFF", brandSub: "rgba(255,255,255,0.75)",
    text: "rgba(255,255,255,0.92)", sub: "rgba(255,255,255,0.6)",
  } : base;
  // Fade to the SAME color with alpha 0 (fading to rgba(0,0,0,0) darkens mid-fade and draws a vertical stripe)
  const hexToRgba0 = (hex) => {
    const h = (hex || "#000000").replace("#", "");
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},0)`;
  };
  const url = `https://${domain}/cert/${cert.code}`;
  const photo = bgImage || "/banners/banner1.png";
  return (
    <div className="cert-card relative overflow-hidden flex" style={{ width: "148mm", height: "105mm", background: bgFull ? "#111" : s.bg }}>
      {bgFull && (
        <>
          <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" crossOrigin="anonymous" />
          {bgScrim && <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.55) 52%, rgba(0,0,0,0.18) 100%)" }} />}
        </>
      )}
      {!bgFull && <div className="absolute rounded-full" style={{ width: "70mm", height: "70mm", left: "-25mm", top: "-25mm", background: s.deco }} />}
      {!bgFull && <div className="absolute rounded-full" style={{ width: "40mm", height: "40mm", left: "45mm", bottom: "-18mm", background: s.deco }} />}

      {/* Left content */}
      <div className="relative flex-1 min-w-0 flex flex-col" style={{ padding: "5.5mm 4mm 5.5mm 7mm" }}>
        <div className="flex items-center gap-2">
          <img src={logoSrc} alt="" className="rounded-lg object-cover bg-white" style={{ width: "8.5mm", height: "8.5mm", padding: "0.6mm" }} crossOrigin="anonymous" />
          <div className="leading-none">
            <span className="font-display font-black" style={{ fontSize: "5mm", color: s.brand }}>CRAFT</span>
            <span className="font-bold uppercase" style={{ fontSize: "2.4mm", letterSpacing: "0.22em", marginLeft: "2mm", color: s.brandSub }}>fast food</span>
          </div>
        </div>

        <div className="font-display font-black uppercase leading-tight" style={{ fontSize: "6.2mm", marginTop: "4.5mm", color: s.title }}>
          Подарочный<br />сертификат
        </div>

        <div className="rounded-2xl w-fit" style={{ padding: "2.2mm 6mm", marginTop: "3.5mm", background: s.amountBg }}>
          <span className="font-display font-black whitespace-nowrap" style={{ fontSize: "9.5mm", color: s.amountText }}>
            {fmt(cert.amount)} <span style={{ fontSize: "4.3mm" }}>сум</span>
          </span>
        </div>

        <div className="bg-white rounded-xl w-fit" style={{ padding: "1.8mm 6mm", marginTop: "2.8mm", border: s.codeBorder }}>
          <span className="font-display font-black text-black whitespace-nowrap" style={{ fontSize: "5mm", letterSpacing: "0.06em" }}>{cert.code}</span>
        </div>

        <div className="font-bold" style={{ fontSize: "2.7mm", marginTop: "3mm", color: s.text }}>
          {cert.expires ? `Действителен до ${cert.expires}` : cert.activation_days ? `Действителен ${cert.activation_days} дн. — обязательно активируйте в Telegram` : "Действителен бессрочно — активируй в Telegram"}
        </div>
        <div className="font-semibold" style={{ fontSize: "2.2mm", marginTop: "0.8mm", color: s.sub }}>Не подлежит обмену на деньги</div>

        <div className="mt-auto flex items-center gap-2 flex-wrap">
          <span className="rounded-full font-black whitespace-nowrap" style={{ fontSize: "2.6mm", padding: "1.2mm 4mm", background: s.pillBg, color: s.pillText }}>{domain}</span>
          <span className="font-bold whitespace-nowrap" style={{ fontSize: "2.6mm", color: s.text }}>Сканируй QR — активируй в Telegram →</span>
        </div>
      </div>

      {/* Right: photo + QR card */}
      <div className="relative shrink-0 overflow-hidden" style={{ width: "54mm" }}>
        {!bgFull && (
          <>
            <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" crossOrigin="anonymous" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${s.fade} 0%, ${hexToRgba0(s.fade)} 55%)` }} />
          </>
        )}
        <div className="absolute bg-white rounded-2xl flex flex-col items-center" style={{ bottom: "4mm", right: "4mm", padding: "2mm 2.4mm 1.6mm", boxShadow: "0 2px 10px rgba(0,0,0,0.35)" }}>
          <img src={`${API}/qr/data?text=${encodeURIComponent(url)}`} alt="QR" style={{ width: "23mm", height: "23mm" }} crossOrigin="anonymous" />
          <div className="font-black text-[#E30000] whitespace-nowrap" style={{ fontSize: "2.1mm", marginTop: "0.6mm" }}>{domain}</div>
        </div>
      </div>
    </div>
  );
};

// Compress an uploaded image to a compact data-URL (keeps 1GB-RAM server happy: stored once per batch)
const compressImage = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const fr = new FileReader();
  fr.onload = () => { img.src = fr.result; };
  fr.onerror = reject;
  img.onload = () => {
    const MAX = 1100;
    const ratio = Math.min(1, MAX / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * ratio);
    canvas.height = Math.round(img.height * ratio);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    resolve(canvas.toDataURL("image/jpeg", 0.82));
  };
  img.onerror = reject;
  fr.readAsDataURL(file);
});

const saveBlob = (blob, name) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
};

function StylePicker({ value, onChange, compact = false }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {Object.entries(CERT_STYLES).map(([key, s]) => (
        <button key={key} type="button" data-testid={`cert-style-${key}`} onClick={() => onChange(key)}
          className={`flex items-center gap-1.5 rounded-xl border-2 font-bold transition ${compact ? "px-2 py-1 text-[11px]" : "px-2.5 py-1.5 text-xs"} ${value === key ? "border-[#FF0000] bg-red-50" : "border-gray-200 bg-white hover:border-gray-300"}`}>
          <span className="w-4 h-4 rounded-full border border-gray-200" style={{ background: s.swatch }} />
          {s.label}
        </button>
      ))}
    </div>
  );
}

// Scaled live preview of the A6 certificate flyer (148mm x 105mm ≈ 559x397px)
function CertPreview({ cert, styleKey, bgImage, bgFull = false, bgScrim = true, domain, logoSrc, label = "Предпросмотр флаера" }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.6);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, (el.clientWidth || 560) / 562));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={wrapRef} className="w-full" data-testid="cert-live-preview">
      <div className="text-xs font-bold text-gray-400 mb-1.5">{label}</div>
      <div style={{ height: `${Math.round(397 * scale)}px`, width: `${Math.round(560 * scale)}px`, overflow: "hidden" }} className="rounded-xl shadow-md">
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: "148mm" }}>
          <CertCard cert={cert} domain={domain} styleKey={styleKey} bgImage={bgImage} bgFull={bgFull} bgScrim={bgScrim} logoSrc={logoSrc} />
        </div>
      </div>
    </div>
  );
}

export default function Certificates() {
  const [data, setData] = useState({ items: [], stats: {}, batches: {} });
  const [amount, setAmount] = useState("");
  const [count, setCount] = useState(1);
  const [activationDays, setActivationDays] = useState("");
  const [newStyle, setNewStyle] = useState("red");
  const [newBg, setNewBg] = useState(null);
  const [newBgFull, setNewBgFull] = useState(false);
  const [newBgScrim, setNewBgScrim] = useState(true);
  const [secret, setSecret] = useState("");
  const [domain, setDomain] = useState("https://craft-oila.uz");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [bcMsg, setBcMsg] = useState("");
  const [bcTarget, setBcTarget] = useState("activated");
  const [bcBusy, setBcBusy] = useState(false);
  const [tgLink, setTgLink] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [editBatch, setEditBatch] = useState(null); // {batchId, style, bg_image}
  const [downloading, setDownloading] = useState(false);
  const [renderJob, setRenderJob] = useState(null); // {certs, single}
  const [logoSrc, setLogoSrc] = useState(LOGO_URL);
  const captureRef = useRef(null);
  const [search, setSearch] = useState("");
  const searchRef = useRef("");
  const [deleteBatch, setDeleteBatch] = useState(null); // {batchId, info, certs}
  const [deletingBatch, setDeletingBatch] = useState(false);

  const load = () => api.get("/admin/certificates", { params: searchRef.current ? { q: searchRef.current } : {} }).then((r) => setData({ batches: {}, ...r.data }));
  const applySearch = (v) => { searchRef.current = (v ?? search).trim(); load(); };
  const clearSearch = () => { setSearch(""); searchRef.current = ""; load(); };
  useEffect(() => {
    load();
    api.get("/admin/certificates-secret").then((r) => setSecret(r.data.secret));
    api.get("/admin/certificates-domain").then((r) => setDomain(r.data.domain)).catch(() => {});
    api.get("/admin/staff-telegram-link").then((r) => setTgLink(r.data)).catch(() => {});
    // inline logo as data-URL so html2canvas export works (cross-origin safe)
    fetch(LOGO_URL).then((r) => r.blob()).then((b) => {
      const fr = new FileReader();
      fr.onload = () => setLogoSrc(fr.result);
      fr.readAsDataURL(b);
    }).catch(() => {});
  }, []);

  const domainShort = domain.replace(/^https?:\/\//, "");
  const batches = data.batches || {};

  const groups = useMemo(() => {
    const map = new Map();
    (data.items || []).forEach((c) => {
      const key = c.batch_id || "old";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    return Array.from(map.entries()).map(([batchId, certs]) => ({
      batchId, certs,
      info: batches[batchId] || { style: "red", bg_image: null, created_at: certs[0]?.created_at },
    })).sort((a, b) => ((b.info.created_at || "") > (a.info.created_at || "") ? 1 : -1));
  }, [data, batches]);

  const create = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/certificates", {
        amount: +amount, count: +count,
        activation_days: activationDays ? +activationDays : null,
        style: newStyle, bg_image: newBg, bg_full: newBgFull, bg_scrim: newBgScrim,
      });
      toast.success(`Создано сертификатов: ${res.items.length}`);
      setSelected(res.items.map((i) => i.id));
      setAmount(""); setCount(1); setNewBg(null); setNewBgFull(false); setNewBgScrim(true);
      load();
    } catch (e) { toast.error("Ошибка создания"); }
    setBusy(false);
  };

  const saveSecret = async () => {
    try {
      await api.put("/admin/certificates-secret", { secret });
      toast.success("Секретный код обновлён");
    } catch (e) { toast.error("Код слишком короткий (мин. 4 символа)"); }
  };

  const saveDomain = async () => {
    try {
      const { data: r } = await api.put("/admin/certificates-domain", { domain });
      setDomain(r.domain);
      toast.success("Домен QR обновлён");
    } catch (e) { toast.error("Домен должен начинаться с http(s)://"); }
  };

  const saveBatchStyle = async () => {
    if (!editBatch) return;
    try {
      await api.put(`/admin/cert-batches/${editBatch.batchId}`, { style: editBatch.style, bg_image: editBatch.bg_image, bg_full: !!editBatch.bg_full, bg_scrim: editBatch.bg_scrim !== false });
      toast.success("Оформление партии обновлено");
      setEditBatch(null);
      load();
    } catch (e) { toast.error("Ошибка сохранения (фото слишком большое?)"); }
  };

  const copyTgLink = () => {
    if (!tgLink?.link) return;
    navigator.clipboard.writeText(tgLink.link);
    toast.success("Ссылка скопирована — отправьте её кассиру");
  };

  const voidCert = async (c) => {
    if (!window.confirm(`Аннулировать сертификат ${c.code}?`)) return;
    try { await api.put(`/admin/certificates/${c.id}/void`); load(); } catch (e) { toast.error("Ошибка"); }
  };
  const forceActivate = async (c) => {
    if (!window.confirm(`Активировать сертификат ${c.code} принудительно?\n\nОбычно клиент активирует сертификат САМ — сканирует QR и подтверждает в Telegram. Используйте принудительную активацию, только если клиент забыл или не может активировать.\n\n${c.activation_days ? `Срок действия (${c.activation_days} дн.) начнёт отсчитываться с сегодняшнего дня.` : "Сертификат станет активированным."}`)) return;
    try {
      await api.post(`/admin/certificates/${c.id}/activate`);
      toast.success(`Сертификат ${c.code} активирован`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "already_activated" ? "Уже активирован" : d === "not_active" ? "Сертификат не активен" : "Ошибка активации");
    }
  };
  const forceActivateBatch = async (batchId, certs) => {
    const pending = certs.filter((c) => c.status === "active" && !c.activated).length;
    if (!pending) { toast.info("В партии нет неактивированных сертификатов"); return; }
    if (!window.confirm(`Активировать всю партию принудительно (${pending} шт.)?\n\nОбычно клиенты активируют сертификаты САМИ — сканируют QR и подтверждают в Telegram. Используйте принудительную активацию, только если клиенты забыли или не могут активировать.\n\nСрок действия начнёт отсчитываться с сегодняшнего дня.`)) return;
    try {
      const { data } = await api.post(`/admin/cert-batches/${batchId}/activate`);
      toast.success(`Активировано сертификатов: ${data.activated}`);
      load();
    } catch (e) { toast.error("Ошибка активации партии"); }
  };
  const del = async (c) => {
    if (!window.confirm(`Удалить сертификат ${c.code}?`)) return;
    await api.delete(`/admin/certificates/${c.id}`);
    setSelected((s) => s.filter((x) => x !== c.id));
    load();
  };

  // Copy public certificate link WITHOUT the leading https:// (e.g. craft-oila.uz/cert/ABC123)
  const copyCertLink = async (c) => {
    const link = `${domainShort.replace(/\/+$/, "")}/cert/${c.code}`;
    const fallbackCopy = () => {
      const ta = document.createElement("textarea");
      ta.value = link; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      let ok = false;
      try { ok = document.execCommand("copy"); } catch (e) { ok = false; }
      document.body.removeChild(ta);
      return ok;
    };
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(link); ok = true; }
    } catch (e) { ok = false; }
    if (!ok) ok = fallbackCopy();
    if (ok) toast.success(`Ссылка скопирована: ${link}`);
    else toast.error(`Не удалось скопировать. Ссылка: ${link}`);
  };

  // Delete whole batch (after modal confirmation)
  const confirmDeleteBatch = async () => {
    if (!deleteBatch) return;
    setDeletingBatch(true);
    try {
      const { data } = await api.delete(`/admin/cert-batches/${deleteBatch.batchId}`);
      const ids = deleteBatch.certs.map((c) => c.id);
      setSelected((s) => s.filter((x) => !ids.includes(x)));
      setOpenGroups((g) => { const n = { ...g }; delete n[deleteBatch.batchId]; return n; });
      if (editBatch?.batchId === deleteBatch.batchId) setEditBatch(null);
      toast.success(`Партия${data.batch_number ? ` №${data.batch_number}` : ""} удалена (${data.deleted} серт.)`);
      setDeleteBatch(null);
      load();
    } catch (e) {
      toast.error("Ошибка удаления партии");
    } finally { setDeletingBatch(false); }
  };

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleGroupSel = (certs) => {
    const ids = certs.map((c) => c.id);
    const all = ids.every((id) => selected.includes(id));
    setSelected((s) => (all ? s.filter((x) => !ids.includes(x)) : Array.from(new Set([...s, ...ids]))));
  };
  const printSel = data.items.filter((i) => selected.includes(i.id));
  const stats = data.stats || {};

  const doPrint = () => {
    document.body.classList.add("print-certs");
    window.print();
    setTimeout(() => document.body.classList.remove("print-certs"), 500);
  };

  // ---- Downloads ----
  const downloadQrZip = async (certs) => {
    setDownloading(true);
    try {
      const zip = new JSZip();
      for (const c of certs) {
        const resp = await fetch(`${API}/public/certificate/${c.code}/qr.png`);
        zip.file(`CRAFT-QR-${c.code}.png`, await resp.blob());
      }
      saveBlob(await zip.generateAsync({ type: "blob" }), `CRAFT-QR-${new Date().toISOString().slice(0, 10)}.zip`);
      toast.success(`Скачано QR-кодов: ${certs.length}`);
    } catch (e) { toast.error("Ошибка скачивания"); }
    setDownloading(false);
  };

  const downloadFlyers = (certs) => {
    if (!certs.length) return;
    setDownloading(true);
    setRenderJob({ certs });
  };

  useEffect(() => {
    if (!renderJob || !captureRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        // Wait for webfonts + every image inside the flyers to be fully loaded,
        // so the exported PNG matches the on-screen preview pixel-for-pixel
        try { await document.fonts.ready; } catch (e) { /* older browsers */ }
        const imgs = Array.from(captureRef.current.querySelectorAll("img"));
        await Promise.all(imgs.map((img) => (img.complete && img.naturalWidth > 0)
          ? Promise.resolve()
          : new Promise((res) => { img.onload = res; img.onerror = res; setTimeout(res, 4000); })));
        await new Promise((r) => setTimeout(r, 150));
        const nodes = captureRef.current.querySelectorAll(".cert-card");
        const blobs = [];
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];
          // html-to-image renders via the browser engine itself (SVG foreignObject) — WYSIWYG,
          // unlike html2canvas which re-draws and shifts text / breaks object-fit backgrounds
          const blob = await toBlob(node, {
            pixelRatio: 2,
            width: node.offsetWidth,
            height: node.offsetHeight,
            style: { margin: "0" },
          });
          blobs.push(blob);
          if (cancelled) return;
        }
        if (blobs.length === 1) {
          saveBlob(blobs[0], `CRAFT-CERT-${renderJob.certs[0].code}.png`);
        } else {
          const zip = new JSZip();
          blobs.forEach((b, i) => zip.file(`CRAFT-CERT-${renderJob.certs[i].code}.png`, b));
          saveBlob(await zip.generateAsync({ type: "blob" }), `CRAFT-CERTS-${new Date().toISOString().slice(0, 10)}.zip`);
        }
        toast.success(`Скачано флаеров: ${blobs.length}`);
      } catch (e) { toast.error("Не удалось создать изображение"); }
      setRenderJob(null);
      setDownloading(false);
    })();
    return () => { cancelled = true; };
  }, [renderJob]); // eslint-disable-line react-hooks/exhaustive-deps

  const broadcast = async () => {
    setBcBusy(true);
    try {
      const { data: r } = await api.post("/admin/certificates/broadcast", { message: bcMsg, target: bcTarget });
      toast.success(`Отправлено: ${r.sent} из ${r.total}`);
      setBcMsg("");
    } catch (e) { toast.error("Ошибка рассылки"); }
    setBcBusy(false);
  };

  const onBgFile = async (e, cb) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const dataUrl = await compressImage(f);
      if (dataUrl.length > 900000) { toast.error("Фото слишком большое"); return; }
      cb(dataUrl);
      toast.success("Фото загружено");
    } catch (err) { toast.error("Не удалось прочитать фото"); }
    e.target.value = "";
  };

  const groupCertsForRender = renderJob ? renderJob.certs : [];

  return (
    <div className="space-y-4" data-testid="admin-certificates">
      <style>{`@media print { @page { size: A6 landscape; margin: 0; } }`}</style>
      <div className="flex items-center justify-between flex-wrap gap-2 no-print">
        <h1 className="font-display font-black text-2xl">Сертификаты</h1>
        <div className="flex gap-2 text-xs font-bold">
          <span className="px-3 py-1.5 rounded-full bg-green-100 text-green-700" data-testid="cert-stat-active">Активных: {stats.active || 0} · {fmt(stats.active_amount)} сум</span>
          <span className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-500">Использовано: {stats.redeemed || 0}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 no-print">
        <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
          <div className="text-sm font-extrabold flex items-center gap-2"><Plus size={15} /> Выпустить сертификаты</div>
          <div className="flex flex-wrap gap-2 items-end">
            <label className="block"><span className="text-xs font-bold text-gray-500">Сумма (сум)</span>
              <input data-testid="cert-amount-input" className="admin-input !w-36" type="number" placeholder="50000" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
            <label className="block"><span className="text-xs font-bold text-gray-500">Кол-во</span>
              <input data-testid="cert-count-input" className="admin-input !w-20" type="number" min="1" max="300" value={count} onChange={(e) => setCount(e.target.value)} /></label>
            <label className="block"><span className="text-xs font-bold text-gray-500">Срок в днях (после активации)</span>
              <input data-testid="cert-activation-days-input" className="admin-input !w-36" type="number" min="1" placeholder="напр. 90" value={activationDays} onChange={(e) => setActivationDays(e.target.value)} /></label>
          </div>
          <div className="space-y-1.5">
            <span className="text-xs font-bold text-gray-500">Цветовая схема флаера</span>
            <StylePicker value={newStyle} onChange={setNewStyle} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="admin-btn admin-btn-ghost text-xs cursor-pointer">
              <ImageIcon size={13} /> {newBg ? "Заменить фото фона" : "Своё фото фона (необязательно)"}
              <input data-testid="cert-bg-upload" type="file" accept="image/*" className="hidden" onChange={(e) => onBgFile(e, setNewBg)} />
            </label>
            {newBg && (
              <span className="flex items-center gap-1.5">
                <img src={newBg} alt="" className="h-9 w-14 object-cover rounded-lg border border-gray-200" />
                <button className="text-red-400 text-xs font-bold" onClick={() => setNewBg(null)}>убрать</button>
              </span>
            )}
            <label className="flex items-center gap-2 text-xs font-bold text-gray-600 cursor-pointer select-none" data-testid="cert-bgfull-toggle">
              <button type="button" role="switch" aria-checked={newBgFull} onClick={() => setNewBgFull(!newBgFull)}
                className={`relative w-9 h-5 rounded-full transition-colors ${newBgFull ? "bg-[#FF0000]" : "bg-gray-300"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${newBgFull ? "left-[18px]" : "left-0.5"}`} />
              </button>
              Фон на весь флаер (без свечения)
            </label>
            {newBgFull && (
              <label className="flex items-center gap-2 text-xs font-bold text-gray-600 cursor-pointer select-none" data-testid="cert-bgscrim-toggle">
                <button type="button" role="switch" aria-checked={newBgScrim} onClick={() => setNewBgScrim(!newBgScrim)}
                  className={`relative w-9 h-5 rounded-full transition-colors ${newBgScrim ? "bg-[#FF0000]" : "bg-gray-300"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${newBgScrim ? "left-[18px]" : "left-0.5"}`} />
                </button>
                Тёмная подложка
              </label>
            )}
            <button data-testid="cert-create-btn" className="admin-btn ml-auto" disabled={busy || !amount || +amount <= 0} onClick={create}><Plus size={14} /> Создать</button>
          </div>
          <p className="text-xs text-gray-400 font-semibold">Срок начинает отсчитываться <b>только после активации сертификата в Telegram</b> (гость сканирует QR). Если гость забыл активировать — активируйте принудительно: кнопка ⚡ у сертификата или «Активировать партию». Пустой срок = бессрочный. Оформление можно поменять позже у каждой партии.</p>
          <CertPreview
            cert={{
              code: "GC-ПРИМЕР1",
              amount: +amount || 50000,
              expires: null,
              activation_days: activationDays ? +activationDays : null,
            }}
            styleKey={newStyle} bgImage={newBg} bgFull={newBgFull} bgScrim={newBgScrim} domain={domainShort} logoSrc={logoSrc}
            label="Предпросмотр флаера (обновляется при изменении настроек)" />
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
          <div className="text-sm font-extrabold flex items-center gap-2"><KeyRound size={15} /> Секретный код погашения</div>
          <div className="flex gap-2">
            <input data-testid="cert-secret-input" className="admin-input flex-1" type="text" autoComplete="off" name="craft-cert-redeem-code"
              value={secret} onChange={(e) => setSecret(e.target.value)} />
            <button data-testid="cert-secret-save-btn" className="admin-btn" onClick={saveSecret}>Сохранить</button>
          </div>
          <div className="text-sm font-extrabold flex items-center gap-2 pt-1"><Globe size={15} /> Домен QR-кодов</div>
          <div className="flex gap-2">
            <input data-testid="cert-domain-input" className="admin-input flex-1" autoComplete="off" name="craft-cert-domain"
              value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="https://craft-oila.uz" />
            <button data-testid="cert-domain-save-btn" className="admin-btn" onClick={saveDomain}>Сохранить</button>
          </div>
          <p className="text-xs text-gray-400 font-semibold">QR ведёт на {domainShort}/cert/КОД — страница активации и погашения. Смена домена не влияет на другие настройки.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3 no-print" data-testid="cert-cashiers-card">
        <div className="text-sm font-extrabold flex items-center gap-2"><MessageCircle size={15} /> Кассиры в Telegram (подтверждение погашения)</div>
        {tgLink?.link ? (
          <div className="flex flex-wrap gap-2 items-center">
            <code className="text-xs bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 font-bold text-gray-600 break-all" data-testid="cert-tg-link">{tgLink.link}</code>
            <button data-testid="cert-tg-link-copy" className="admin-btn" onClick={copyTgLink}><Copy size={14} /> Копировать</button>
            <a className="admin-btn" href={tgLink.link} target="_blank" rel="noreferrer">Открыть</a>
          </div>
        ) : (
          <p className="text-xs text-orange-500 font-bold">Telegram-бот не настроен (нет токена) — ссылка появится после подключения бота.</p>
        )}
        <p className="text-xs text-gray-400 font-semibold">Кассир открывает ссылку → жмёт Start → получает запросы «Подтвердить погашение» прямо в Telegram. Также подключиться можно из кабинета кассира (/staff).</p>
        {tgLink?.cashiers?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tgLink.cashiers.map((c, i) => (
              <span key={c.id || i} className="pl-2.5 pr-1 py-1 rounded-full bg-green-50 text-green-700 text-xs font-bold inline-flex items-center gap-1" data-testid={`cert-cashier-${i}`}>
                ✓ {c.name}{c.telegram_username ? ` (@${c.telegram_username})` : ""}
                <button
                  data-testid={`cert-cashier-delete-${i}`}
                  title="Удалить кассира"
                  className="w-4 h-4 rounded-full bg-green-100 hover:bg-red-100 hover:text-red-600 flex items-center justify-center leading-none"
                  onClick={async () => {
                    if (!window.confirm(`Удалить кассира «${c.name}»? Он перестанет получать запросы подтверждения в Telegram.`)) return;
                    try {
                      await api.delete(`/admin/staff/${c.id}`);
                      setTgLink({ ...tgLink, cashiers: tgLink.cashiers.filter((x) => x.id !== c.id) });
                      toast.success(`Кассир «${c.name}» удалён`);
                    } catch (e) { toast.error("Не удалось удалить кассира"); }
                  }}
                >×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3 no-print" data-testid="cert-broadcast-card">
        <div className="text-sm font-extrabold flex items-center gap-2"><Send size={15} /> Рассылка держателям сертификатов (Telegram)</div>
        <textarea data-testid="cert-broadcast-msg" className="admin-input" rows={2} placeholder="Например: Напоминаем — ваш подарочный сертификат CRAFT скоро истекает! Ждём вас 🍔"
          value={bcMsg} onChange={(e) => setBcMsg(e.target.value)} />
        <div className="flex flex-wrap gap-2 items-center">
          <select data-testid="cert-broadcast-target" className="admin-input !w-64" value={bcTarget} onChange={(e) => setBcTarget(e.target.value)}>
            <option value="activated">Все активированные (активные)</option>
            <option value="expiring7">Истекают в течение 7 дней</option>
          </select>
          <button data-testid="cert-broadcast-btn" className="admin-btn" disabled={bcBusy || !bcMsg.trim()} onClick={broadcast}><Send size={14} /> Отправить</button>
        </div>
        <p className="text-xs text-gray-400 font-semibold">Получат только гости, активировавшие сертификат через Telegram. Напоминание за 7 дней до истечения отправляется автоматически.</p>
      </div>

      {/* Groups toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2 no-print">
        <div className="text-sm font-extrabold">Все сертификаты ({data.total || data.items.length}) — по партиям</div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex items-center gap-1.5">
            <input
              data-testid="cert-search-input"
              className="admin-input !h-9 !w-56 text-sm"
              placeholder="Поиск по № партии или коду"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
            />
            <button data-testid="cert-search-btn" className="admin-btn admin-btn-ghost !h-9" onClick={() => applySearch()}>
              <Search size={14} /> Найти
            </button>
            {searchRef.current ? (
              <button data-testid="cert-search-clear" className="admin-btn admin-btn-ghost !h-9" onClick={clearSearch}>Сброс</button>
            ) : null}
          </div>
          <button data-testid="cert-print-btn" className="admin-btn admin-btn-ghost" disabled={!printSel.length || downloading} onClick={doPrint}>
            <Printer size={14} /> Печать ({printSel.length})
          </button>
          <button data-testid="cert-download-flyers-btn" className="admin-btn admin-btn-ghost" disabled={!printSel.length || downloading} onClick={() => downloadFlyers(printSel)}>
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <FileImage size={14} />} Флаеры ({printSel.length})
          </button>
          <button data-testid="cert-download-qrs-btn" className="admin-btn admin-btn-ghost" disabled={!printSel.length || downloading} onClick={() => downloadQrZip(printSel)}>
            <QrCode size={14} /> QR-коды ({printSel.length})
          </button>
        </div>
      </div>

      {/* Batch groups */}
      <div className="space-y-3 no-print">
        {groups.map(({ batchId, certs, info }) => {
          const open = openGroups[batchId] !== false; // open by default
          const allSel = certs.every((c) => selected.includes(c.id));
          const styleInfo = CERT_STYLES[info.style] || CERT_STYLES.red;
          const isEdit = editBatch?.batchId === batchId;
          return (
            <div key={batchId} className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid={`cert-group-${batchId}`}>
              <div className="flex items-center gap-2 p-3.5 border-b border-gray-100 flex-wrap">
                <button onClick={() => setOpenGroups({ ...openGroups, [batchId]: !open })} className="p-1 text-gray-400" data-testid={`cert-group-toggle-${batchId}`}>
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                <div className="min-w-0">
                  <div className="text-sm font-extrabold">
                    {info.batch_number ? `Партия №${info.batch_number}` : "Партия"} · от {fmtD(info.created_at || certs[0]?.created_at)} · {fmt(certs[0]?.amount)} сум · {certs.length} шт
                  </div>
                  <div className="text-[11px] font-bold text-gray-400 flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full border border-gray-200 inline-block" style={{ background: styleInfo.swatch }} />
                    {styleInfo.label}{info.bg_image ? " · своё фото" : ""}{info.bg_full ? " · полный фон" : ""}
                  </div>
                </div>
                <div className="ml-auto flex gap-1.5 flex-wrap">
                  <button className="admin-btn admin-btn-ghost !h-8 text-xs" onClick={() => toggleGroupSel(certs)} data-testid={`cert-group-select-${batchId}`}>
                    {allSel ? <CheckSquare size={13} className="text-[#FF0000]" /> : <Square size={13} />} {allSel ? "Снять" : "Выбрать все"}
                  </button>
                  <button className="admin-btn admin-btn-ghost !h-8 text-xs" disabled={downloading} onClick={() => downloadFlyers(certs)} data-testid={`cert-group-flyers-${batchId}`}>
                    <FileImage size={13} /> Флаеры
                  </button>
                  <button className="admin-btn admin-btn-ghost !h-8 text-xs" disabled={downloading} onClick={() => downloadQrZip(certs)} data-testid={`cert-group-qrs-${batchId}`}>
                    <QrCode size={13} /> QR
                  </button>
                  {batchId !== "old" && (
                    <button className="admin-btn admin-btn-ghost !h-8 text-xs" data-testid={`cert-group-activate-${batchId}`}
                      onClick={() => forceActivateBatch(batchId, certs)}>
                      <Zap size={13} /> Активировать партию
                    </button>
                  )}
                  {batchId !== "old" && (
                    <button className="admin-btn admin-btn-ghost !h-8 text-xs" data-testid={`cert-group-style-${batchId}`}
                      onClick={() => setEditBatch(isEdit ? null : { batchId, style: info.style || "red", bg_image: info.bg_image || null, bg_full: !!info.bg_full, bg_scrim: info.bg_scrim !== false })}>
                      <ImageIcon size={13} /> Оформление
                    </button>
                  )}
                  {batchId !== "old" && (
                    <button className="admin-btn admin-btn-ghost !h-8 text-xs !text-red-500 hover:!bg-red-50" data-testid={`cert-group-delete-${batchId}`}
                      title="Удалить всю партию" onClick={() => setDeleteBatch({ batchId, info, certs })}>
                      <Trash2 size={13} /> Удалить партию
                    </button>
                  )}
                </div>
              </div>
              {isEdit && (
                <div className="p-3.5 bg-gray-50 border-b border-gray-100 space-y-2" data-testid="cert-batch-edit">
                  <StylePicker compact value={editBatch.style} onChange={(v) => setEditBatch({ ...editBatch, style: v })} />
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="admin-btn admin-btn-ghost text-xs cursor-pointer">
                      <ImageIcon size={13} /> {editBatch.bg_image ? "Заменить фото" : "Загрузить фото фона"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => onBgFile(e, (d) => setEditBatch({ ...editBatch, bg_image: d }))} />
                    </label>
                    {editBatch.bg_image && (
                      <span className="flex items-center gap-1.5">
                        <img src={editBatch.bg_image} alt="" className="h-9 w-14 object-cover rounded-lg border border-gray-200" />
                        <button className="text-red-400 text-xs font-bold" onClick={() => setEditBatch({ ...editBatch, bg_image: null })}>убрать</button>
                      </span>
                    )}
                    <label className="flex items-center gap-2 text-xs font-bold text-gray-600 cursor-pointer select-none" data-testid="cert-batch-bgfull-toggle">
                      <button type="button" role="switch" aria-checked={!!editBatch.bg_full} onClick={() => setEditBatch({ ...editBatch, bg_full: !editBatch.bg_full })}
                        className={`relative w-9 h-5 rounded-full transition-colors ${editBatch.bg_full ? "bg-[#FF0000]" : "bg-gray-300"}`}>
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${editBatch.bg_full ? "left-[18px]" : "left-0.5"}`} />
                      </button>
                      Фон на весь флаер (без свечения)
                    </label>
                    {editBatch.bg_full && (
                      <label className="flex items-center gap-2 text-xs font-bold text-gray-600 cursor-pointer select-none" data-testid="cert-batch-bgscrim-toggle">
                        <button type="button" role="switch" aria-checked={editBatch.bg_scrim !== false} onClick={() => setEditBatch({ ...editBatch, bg_scrim: editBatch.bg_scrim === false })}
                          className={`relative w-9 h-5 rounded-full transition-colors ${editBatch.bg_scrim !== false ? "bg-[#FF0000]" : "bg-gray-300"}`}>
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${editBatch.bg_scrim !== false ? "left-[18px]" : "left-0.5"}`} />
                        </button>
                        Тёмная подложка
                      </label>
                    )}
                    <button className="admin-btn !h-8 text-xs ml-auto" onClick={saveBatchStyle} data-testid="cert-batch-style-save">Сохранить оформление</button>
                  </div>
                  <div className="max-w-[560px]">
                    <CertPreview cert={certs[0]} styleKey={editBatch.style} bgImage={editBatch.bg_image} bgFull={!!editBatch.bg_full} bgScrim={editBatch.bg_scrim !== false} domain={domainShort} logoSrc={logoSrc} />
                  </div>
                </div>
              )}
              {open && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-gray-400 uppercase">
                      <th className="p-3"></th><th className="p-3">Код</th><th className="p-3">Статус</th><th className="p-3">Срок</th><th className="p-3">Telegram</th><th className="p-3">Использован</th><th className="p-3"></th>
                    </tr></thead>
                    <tbody>
                      {certs.map((c) => {
                        const expired = c.expires && c.expires < new Date().toISOString().slice(0, 10);
                        const [label, cls] = c.status === "active" && expired ? ["Истёк", "bg-orange-100 text-orange-600"] : STATUS[c.status] || [c.status, "bg-gray-100"];
                        return (
                          <tr key={c.id} className="border-t border-gray-50 hover:bg-gray-50" data-testid={`cert-row-${c.code}`}>
                            <td className="p-3">
                              <button data-testid={`cert-select-${c.code}`} onClick={() => toggleSel(c.id)} className="text-gray-500">
                                {selected.includes(c.id) ? <CheckSquare size={17} className="text-[#FF0000]" /> : <Square size={17} />}
                              </button>
                            </td>
                            <td className="p-3 font-black tracking-wider">
                              <div className="flex items-center gap-1.5">
                                <span data-testid={`cert-code-${c.code}`}>{c.code}</span>
                                <button data-testid={`cert-link-copy-${c.code}`} title={`Скопировать ссылку ${domainShort}/cert/${c.code}`} onClick={() => copyCertLink(c)}
                                  className="p-1 rounded-md text-gray-400 hover:text-[#229ED9] hover:bg-blue-50 transition-colors"><Link2 size={13} /></button>
                              </div>
                            </td>
                            <td className="p-3"><span className={`px-2.5 py-1 rounded-full text-xs font-bold ${cls}`}>{label}</span></td>
                            <td className="p-3 text-xs text-gray-500">{c.expires ? c.expires : c.activation_days ? `${c.activation_days} дн. после активации` : "—"}</td>
                            <td className="p-3 text-xs font-bold">{c.activated ? <span className="text-green-600">✓ {c.activated_via === "admin" ? "принудительно" : c.holder_name || ""}</span> : <span className="text-gray-300">—</span>}</td>
                            <td className="p-3 text-xs text-gray-500" data-testid={`cert-redeemed-at-${c.code}`}>
                              {c.redeemed_at ? `${fmtDT(c.redeemed_at)} · ${c.redeemed_via === "staff" ? c.redeemed_by || "кассир" : c.redeemed_via === "telegram" ? `TG: ${c.redeemed_by || "кассир"}` : "секретный код"}` : "—"}
                            </td>
                            <td className="p-3">
                              <div className="flex gap-1.5 justify-end">
                                <button data-testid={`cert-flyer-download-${c.code}`} title="Скачать флаер (PNG)" disabled={downloading} onClick={() => downloadFlyers([c])}
                                  className="p-1.5 bg-gray-100 rounded-lg text-gray-700 hover:bg-[#FEBC10]/30"><FileImage size={13} /></button>
                                <a data-testid={`cert-qr-download-${c.code}`} title="Скачать QR" href={`${API}/public/certificate/${c.code}/qr.png?download=1`}
                                  className="p-1.5 bg-gray-100 rounded-lg text-gray-700 hover:bg-[#FEBC10]/30"><Download size={13} /></a>
                                {c.status === "active" && !c.activated && (
                                  <button data-testid={`cert-activate-${c.code}`} title="Активировать принудительно (если клиент забыл)" onClick={() => forceActivate(c)}
                                    className="p-1.5 bg-gray-100 rounded-lg text-[#229ED9] hover:bg-blue-50"><Zap size={13} /></button>
                                )}
                                {c.status === "active" && <button data-testid={`cert-void-${c.code}`} title="Аннулировать" onClick={() => voidCert(c)} className="p-1.5 bg-gray-100 rounded-lg text-orange-500"><Ban size={13} /></button>}
                                <button title="Удалить" onClick={() => del(c)} className="p-1.5 bg-gray-100 rounded-lg text-red-500"><Trash2 size={13} /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
        {!data.items.length && <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-400 font-bold text-sm">Пока нет сертификатов — создайте первую партию выше</div>}
      </div>

      {/* Delete batch confirmation modal */}
      {deleteBatch && (() => {
        const total = deleteBatch.certs.length;
        const redeemed = deleteBatch.certs.filter((c) => c.status === "redeemed").length;
        const activated = deleteBatch.certs.filter((c) => c.activated && c.status !== "redeemed").length;
        const num = deleteBatch.info?.batch_number;
        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm no-print" data-testid="cert-batch-delete-modal"
            onClick={() => !deletingBatch && setDeleteBatch(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center shrink-0"><AlertTriangle size={20} /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-black">Удалить {num ? `партию №${num}` : "партию"}?</div>
                  <div className="text-xs font-bold text-gray-400 mt-0.5">
                    от {fmtD(deleteBatch.info?.created_at || deleteBatch.certs[0]?.created_at)} · {fmt(deleteBatch.certs[0]?.amount)} сум · {total} шт
                  </div>
                </div>
                <button className="p-1 text-gray-400 hover:text-gray-600" onClick={() => setDeleteBatch(null)} disabled={deletingBatch} data-testid="cert-batch-delete-close"><X size={16} /></button>
              </div>
              <div className="text-sm text-gray-600 font-semibold space-y-2">
                <p>Будут безвозвратно удалены все <b>{total}</b> сертификатов этой партии. QR-коды и ссылки с флаеров перестанут работать.</p>
                {(redeemed > 0 || activated > 0) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 space-y-0.5" data-testid="cert-batch-delete-warning">
                    {redeemed > 0 && <div>• Погашено сертификатов: <b>{redeemed}</b> — история погашения будет потеряна.</div>}
                    {activated > 0 && <div>• Активировано клиентами, но не использовано: <b>{activated}</b>.</div>}
                  </div>
                )}
                <p className="text-xs text-gray-400">Действие необратимо.</p>
              </div>
              <div className="flex gap-2 justify-end">
                <button className="admin-btn admin-btn-ghost" onClick={() => setDeleteBatch(null)} disabled={deletingBatch} data-testid="cert-batch-delete-cancel">Отмена</button>
                <button className="admin-btn !bg-red-500 hover:!bg-red-600 !text-white" onClick={confirmDeleteBatch} disabled={deletingBatch} data-testid="cert-batch-delete-confirm">
                  {deletingBatch ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Удалить партию
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Print preview (selected) */}
      {printSel.length > 0 && (
        <div className="cert-print-area space-y-4" data-testid="cert-print-preview">
          <div className="text-sm font-extrabold text-gray-500 no-print">Предпросмотр печати ({printSel.length}) — формат А6 горизонтальный</div>
          <div className="flex flex-col gap-5 items-center">
            {printSel.map((c) => {
              const info = batches[c.batch_id] || {};
              return <CertCard key={c.id} cert={c} domain={domainShort} styleKey={info.style || "red"} bgImage={info.bg_image} bgFull={!!info.bg_full} bgScrim={info.bg_scrim !== false} logoSrc={logoSrc} />;
            })}
          </div>
        </div>
      )}

      {/* Hidden render area for PNG export */}
      {renderJob && (
        <div ref={captureRef} style={{ position: "fixed", left: "-9999px", top: 0, zIndex: -1 }} aria-hidden="true">
          {groupCertsForRender.map((c) => {
            const info = batches[c.batch_id] || {};
            return <CertCard key={c.id} cert={c} domain={domainShort} styleKey={info.style || "red"} bgImage={info.bg_image} bgFull={!!info.bg_full} bgScrim={info.bg_scrim !== false} logoSrc={logoSrc} />;
          })}
        </div>
      )}
    </div>
  );
}
